import useSWR, { mutate } from "swr";
import upperFirst from "lodash/upperFirst";

import { ApiClientFactory } from "common/middleware/api.client-factory";
import { NotificationType as DefaultNotificationType } from "common/middleware/api.services";

import { getLocale } from "common/middleware/localization";
import { filterOutNumericKeys } from "common/utils/filterOutNumericKeys";
type NotificationStatus = "read" | "unread";

export enum NotificationType {
  ListingIncompleteReminder = DefaultNotificationType.ListingIncompleteReminder,
  ListingDenied = DefaultNotificationType.ListingDenied,
  ListingChangesDenied = DefaultNotificationType.ListingChangesDenied,
  ListingApproved = DefaultNotificationType.ListingApproved,
  ListingChangesAccepted = DefaultNotificationType.ListingChangesAccepted,
  OfferAcceptedSeller = DefaultNotificationType.OfferAcceptedSeller,
  DealSellerSignatureRequired = DefaultNotificationType.DealSellerSignatureRequired,
  SignScanned = DefaultNotificationType.SignScanned,
  ListingInviteSeller = DefaultNotificationType.ListingInviteSeller,
  OfferNewReceived = DefaultNotificationType.OfferNewReceived,
  OfferAcceptedBuyer = DefaultNotificationType.OfferAcceptedBuyer,
  OfferExpiring = DefaultNotificationType.OfferExpiring,
  OfferExpired = DefaultNotificationType.OfferExpired,
  OfferDeclined = DefaultNotificationType.OfferDeclined,
  OfferExtensionReceived = DefaultNotificationType.OfferExtensionReceived,
  OfferExtensionApproved = DefaultNotificationType.OfferExtensionApproved,
  OfferExtensionDeclined = DefaultNotificationType.OfferExtensionDeclined,
  OpportunityInviteReceived = DefaultNotificationType.OpportunityInviteReceived,
  OpportunityCreated = DefaultNotificationType.OpportunityCreated,
  ListingChangesBuyerUpdate = DefaultNotificationType.ListingChangesBuyerUpdate,
  OfferCancelledNotice = DefaultNotificationType.OfferCancelledNotice,
  DealInviteBuyer = DefaultNotificationType.DealInviteBuyer,
  DealBuyerSignatureRequired = DefaultNotificationType.DealBuyerSignatureRequired,
  DealSignedComplete = DefaultNotificationType.DealSignedComplete,
  DealCanceledNotice = DefaultNotificationType.DealCanceledNotice,
  DealCanceledConfirmation = DefaultNotificationType.DealCanceledConfirmation,
  ChatRejoinRequested = DefaultNotificationType.ChatRejoinRequested,
  CancellationContractBuyerSignatureRequired = DefaultNotificationType.CancellationContractBuyerSignatureRequired,
  CancellationContractSellerSignatureRequired = DefaultNotificationType.CancellationContractSellerSignatureRequired,
  DealSetToFirm = DefaultNotificationType.DealSetToFirm,
  FulfillmentContractSellerSignatureRequired = DefaultNotificationType.FulfillmentContractSellerSignatureRequired,
  FulfillmentContractFullySigned = DefaultNotificationType.FulfillmentContractFullySigned,
  AppointmentRequested = DefaultNotificationType.AppointmentRequested,
  AppointmentAccepted = DefaultNotificationType.AppointmentAccepted,
  AppointmentDeclined = DefaultNotificationType.AppointmentDeclined,
  AppointmentRescheduleProposed = DefaultNotificationType.AppointmentRescheduleProposed,
  AppointmentRescheduleAccepted = DefaultNotificationType.AppointmentRescheduleAccepted,
  AppointmentRescheduleDeclined = DefaultNotificationType.AppointmentRescheduleDeclined,
  AppointmentCancelled = DefaultNotificationType.AppointmentCancelled,
  AppointmentReminder = DefaultNotificationType.AppointmentReminder,
  OpenHouseUpdated = DefaultNotificationType.OpenHouseUpdated,
  OpenHouseCancelled = DefaultNotificationType.OpenHouseCancelled,
  OpenHouseNewRsvp = DefaultNotificationType.OpenHouseNewRsvp,
  OpenHouseRsvpCancelled = DefaultNotificationType.OpenHouseRsvpCancelled,
};

const notificationTypes = filterOutNumericKeys(NotificationType);

const getUserNotifications = async (
  key: string,
  pageNumber: number,
  pageSize: number = 0, // 0 fetches all notifications
  statusFilter: NotificationStatus,
) => {
  const client = await ApiClientFactory.getUserNotificationsClient();

  const notifications = await client.getNotifications(
    pageNumber,
    pageSize,
    ["read", "unread"].includes(statusFilter) ? statusFilter === "read" : null,
    getLocale(),
  );

  return notifications?.entities?.filter(n => notificationTypes.includes(upperFirst(n.type.toString())));
}

export interface UseUserNotificationsParams {
  pageNumber?: number,
  pageSize?: number,
  statusFilter?: NotificationStatus,
}

const USER_NOTIFICATIONS_KEY = "/api/userNotifications";

export const useUserNotifications = (args?: UseUserNotificationsParams) => {
  const { data, error, mutate } = useSWR(
    [USER_NOTIFICATIONS_KEY,
      args?.pageNumber,
      args?.pageSize,
      args?.statusFilter,
    ],
    getUserNotifications
  );

  return {
    notifications: data,
    areNotificationsLoading: !error && !data,
    error,
    mutateNotifications: mutate,
  };
}

export const mutateUserNotifications = async (args?: UseUserNotificationsParams) => {
  await mutate([USER_NOTIFICATIONS_KEY,
    args?.pageNumber,
    args?.pageSize,
    args?.statusFilter,
  ]);
}
